/**
 * Create and join flows shared by the home screen, the join route and the QR
 * entry point.
 *
 * Extracted from CreateOrJoinEvent so every entry point produces exactly the
 * same event document shape and the same store bindings. Event initialization
 * (`initializeEventSchema`, `sync/queue`, `sync/player`) is unchanged.
 */
import { arrayUnion, doc, getDoc, runTransaction, setDoc, updateDoc } from 'firebase/firestore';
import { db, ensureAnonymousAuth, getAuthUid } from '../firebase';
import useEventStore from '../stores/eventStore';
import { useRoleStore } from '../stores/roleStore';
import { canResumeEvent, findParticipant, buildReconciledHostParticipants, reconcileOwnGuestParticipant } from '../utils/identity';
import { isIntentionalLeave, toMillis } from '../utils/sessionHistory';
import { canHostParty, resolveDisplayName, sanitizeParticipantPhotoURL } from '../utils/authIdentity';
import { generateEventCode, normalizeEventCode } from '../utils/eventCode';
import {
  EVENT_STATUS,
  JOIN_MODE,
  JOIN_REQUEST_STATUS,
  isEventLive,
  isJoinApprovalRequired,
  normalizeJoinMode,
} from '../utils/eventStatus';
import { clearSessionEventId, loadSessionEventId, saveSessionEventId } from './sessionRef';
import { initializeEventSchema } from './sessionSync';
import { getCurrentUser } from './authProviders';
import { rememberSessionMembership } from './authSession';
import { fetchSessionEntry, markSessionLeft } from './sessionHistory';
import { markPresenceOffline, markPresenceOnline } from './presence';
import { describeError } from '../utils/errorLog';
import { cleanupStores } from '../utils/storeUtils';
import useUserStore from '../stores/userStore';
import { VIBE_PARTY_SCHEMA } from '../vibe/legacy';
import { buildVibePreference, vibePreferenceRef } from '../vibe/partyVibe';
import { canonicalizeVibeSelection, isCompleteVibeSelection } from '../vibe/selection';
import {
  buildJoinRequestPayload,
  eventEndedPayload,
  fetchJoinRequest,
  joinRequestRef,
} from './joinRequests';

const CODE_COLLISION_ATTEMPTS = 5;

export class EventLifecycleError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'EventLifecycleError';
    this.code = code;
  }
}

async function requireAuthUid() {
  const user = await ensureAnonymousAuth();
  const uid = user?.uid || getAuthUid();
  if (!uid) {
    throw new EventLifecycleError('not-signed-in', 'Not signed in');
  }
  // Keep the role store's uid authoritative here too, so create/join do not
  // depend on the auth bootstrap having run first.
  useRoleStore.getState().setAuthenticatedUser(uid);
  return uid;
}

export function enterEvent(eventData, hostId, participantRole) {
  useEventStore.getState().setEventData(eventData);
  useRoleStore.getState().bindEventIdentity(hostId, participantRole);
  saveSessionEventId(eventData.id);
}

/** Reserve an unused event code. Retries on the (rare) collision. */
export async function reserveEventCode(attempts = CODE_COLLISION_ATTEMPTS) {
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const code = generateEventCode();
    const existing = await getDoc(doc(db, 'events', code));
    if (!existing?.exists?.()) return code;
  }
  throw new EventLifecycleError('code-unavailable', 'Could not allocate an event code');
}

export function buildEventPayload({
  code,
  uid,
  title,
  hostName,
  guestRequestsEnabled,
  joinMode = JOIN_MODE.open,
  photoURL = null,
  createdAt = Date.now(),
}) {
  return {
    id: code,
    title: title.trim(),
    hostId: uid,
    createdAt,
    schemaVersion: 2,
    status: EVENT_STATUS.active,
    queue: [],
    settings: {
      voteSkipThreshold: 50,
      guestRequestsEnabled: guestRequestsEnabled !== false,
      joinMode: normalizeJoinMode(joinMode),
      vibeSchema: VIBE_PARTY_SCHEMA,
    },
    participants: [{
      id: uid,
      name: hostName,
      role: 'host',
      joinedAt: Date.now(),
      photoURL: sanitizeParticipantPhotoURL(photoURL),
    }],
    participantIds: [uid],
    // Append-only historical membership. Leave removes participantIds so vibe
    // rules drop access, and this list is what authorizes an open-party rejoin
    // to restore that access without scanning participant maps.
    joinedParticipantIds: [uid],
  };
}

/**
 * Host a new party.
 *
 * Requires a Google-backed account: an anonymous visitor is asked to upgrade
 * first (3.7). `hostId` is always the authenticated Firebase uid.
 */
export async function createParty({
  title,
  guestRequestsEnabled = true,
  joinMode = JOIN_MODE.open,
  hostName = null,
  vibe = null,
}) {
  if (!title || !title.trim()) {
    throw new EventLifecycleError('missing-title', 'Please name your party');
  }

  const uid = await requireAuthUid();
  const user = getCurrentUser();
  const storeUser = useUserStore.getState().user;
  const storeProfile = useUserStore.getState().profile;

  if (!canHostParty(user)) {
    throw new EventLifecycleError('google-required', 'Sign in with Google to host a party');
  }

  const selection = canonicalizeVibeSelection(vibe);
  if (!isCompleteVibeSelection(selection)) {
    throw new EventLifecycleError('missing-feel', 'Choose how the party should feel');
  }

  const code = await reserveEventCode();
  const createdAt = Date.now();
  const eventData = buildEventPayload({
    code,
    uid,
    title,
    hostName: resolveDisplayName(user, hostName, {
      fallbackNames: [storeUser?.displayName, storeProfile?.displayName],
    }),
    guestRequestsEnabled,
    joinMode,
    photoURL: sanitizeParticipantPhotoURL(user?.photoURL || storeUser?.photoURL),
    createdAt,
  });
  const hostVibe = buildVibePreference(selection, {
    submittedAt: createdAt,
    updatedAt: createdAt,
  });

  await runTransaction(db, async (transaction) => {
    transaction.set(doc(db, 'events', code), eventData);
    transaction.set(vibePreferenceRef(code, uid), hostVibe);
  });
  await initializeEventSchema(code, { legacyQueue: [] });

  enterEvent(eventData, uid, 'host');
  rememberSessionMembership(eventData, 'host').catch(() => {});
  markPresenceOnline({ eventId: eventData.id, uid }).catch(() => {});

  return eventData;
}

/** Look up an event without joining it, for the join screen's party name. */
export async function fetchEventByCode(rawCode) {
  const code = normalizeEventCode(rawCode);
  if (!code) return null;

  const snapshot = await getDoc(doc(db, 'events', code));
  if (!snapshot?.exists?.()) return null;
  return { id: snapshot.id, ...snapshot.data() };
}

async function restoreVibeMembership(eventData, uid) {
  if (
    !Array.isArray(eventData.participantIds)
    || eventData.participantIds.includes(uid)
    || !Array.isArray(eventData.joinedParticipantIds)
    || !eventData.joinedParticipantIds.includes(uid)
  ) {
    return eventData;
  }

  const participantIds = [...eventData.participantIds, uid];
  try {
    await updateDoc(doc(db, 'events', eventData.id), { participantIds });
    return { ...eventData, participantIds };
  } catch (error) {
    console.error('Failed to restore vibe membership:', describeError(error));
    return eventData;
  }
}

async function enterExistingParticipant(eventData, uid, identity = {}) {
  const isHost = uid === eventData.hostId;
  let nextEvent = await restoreVibeMembership(eventData, uid);

  if (
    isEventLive(eventData)
    && !isHost
    && identity.name
    && !isJoinApprovalRequired(eventData)
  ) {
    const nextParticipants = reconcileOwnGuestParticipant(eventData.participants, {
      uid,
      hostId: eventData.hostId,
      name: identity.name,
      photoURL: identity.photoURL,
    });
    if (nextParticipants) {
      try {
        await updateDoc(doc(db, 'events', eventData.id), { participants: nextParticipants });
        nextEvent = { ...eventData, participants: nextParticipants };
      } catch (error) {
        console.error('Failed to reconcile guest identity:', describeError(error));
      }
    }
  }

  const existing = findParticipant(nextEvent.participants, uid);
  const role = isHost ? 'host' : existing?.role || 'guest';
  enterEvent(nextEvent, nextEvent.hostId, existing?.role);
  rememberSessionMembership(nextEvent, isHost ? 'host' : 'guest').catch(() => {});
  if (isEventLive(nextEvent)) {
    markPresenceOnline({ eventId: nextEvent.id, uid }).catch(() => {});
  }
  return { eventData: nextEvent, role, rejoined: true, pending: false };
}

/**
 * An approved request created before Leave Party must not skip the door.
 * A host Allow after a new pending request has resolvedAt after leftAt.
 */
export function isStaleApprovedJoinRequest(request, sessionEntry) {
  if (request?.status !== JOIN_REQUEST_STATUS.approved) return false;
  if (!isIntentionalLeave(sessionEntry)) return false;
  const leftAt = toMillis(sessionEntry.leftAt) ?? 0;
  const requestTime = toMillis(request.resolvedAt) ?? toMillis(request.createdAt) ?? 0;
  return requestTime <= leftAt;
}

async function writePendingJoinRequest(eventData, uid, name, user) {
  const payload = buildJoinRequestPayload({
    uid,
    name,
    photoURL: user?.photoURL,
  });
  if (!payload) {
    throw new EventLifecycleError('missing-name', 'Please enter your name');
  }
  await setDoc(joinRequestRef(eventData.id, uid), payload);
  return {
    eventData,
    role: 'guest',
    rejoined: false,
    pending: true,
    joinStatus: JOIN_REQUEST_STATUS.pending,
  };
}

async function submitApprovalJoin(eventRef, eventData, uid, name, user, sessionEntry) {
  const existingRequest = await fetchJoinRequest(eventData.id, uid);

  if (existingRequest?.status === JOIN_REQUEST_STATUS.denied) {
    throw new EventLifecycleError('join-denied', 'The host declined your request to join');
  }

  if (isStaleApprovedJoinRequest(existingRequest, sessionEntry)) {
    return writePendingJoinRequest(eventData, uid, name, user);
  }

  if (existingRequest?.status === JOIN_REQUEST_STATUS.approved) {
    const fresh = await getDoc(eventRef);
    const latest = fresh?.exists?.() ? { id: fresh.id, ...fresh.data() } : eventData;
    if (findParticipant(latest.participants, uid) || uid === latest.hostId) {
      return enterExistingParticipant(latest, uid, {
        name,
        photoURL: user?.photoURL,
      });
    }
    return { eventData: latest, role: 'guest', rejoined: false, pending: true, joinStatus: JOIN_REQUEST_STATUS.approved };
  }

  if (existingRequest?.status === JOIN_REQUEST_STATUS.pending) {
    return {
      eventData,
      role: 'guest',
      rejoined: false,
      pending: true,
      joinStatus: JOIN_REQUEST_STATUS.pending,
    };
  }

  return writePendingJoinRequest(eventData, uid, name, user);
}

/**
 * Join an existing party as guest (or re-enter as host / returning participant).
 * Works for anonymous and Google users alike; the provider never changes role.
 *
 * When `settings.joinMode` is `approval`, a new guest writes a join request
 * instead of appending themselves to `participants`.
 */
export async function joinParty({ eventCode, displayName = '' }) {
  const code = normalizeEventCode(eventCode);
  if (!code) {
    throw new EventLifecycleError('missing-code', 'Please enter an event code');
  }

  const uid = await requireAuthUid();
  const user = getCurrentUser();
  const name = resolveDisplayName(user, displayName);

  const eventRef = doc(db, 'events', code);
  const eventDoc = await getDoc(eventRef);

  if (!eventDoc?.exists?.()) {
    throw new EventLifecycleError('not-found', 'Event not found');
  }

  const eventData = { id: eventDoc.id, ...eventDoc.data() };
  const existing = findParticipant(eventData.participants, uid);
  const isHost = uid === eventData.hostId;
  const sessionEntry = await fetchSessionEntry(uid, eventData.id);
  const leftLive = isEventLive(eventData) && !isHost && isIntentionalLeave(sessionEntry);

  if (!isEventLive(eventData) && !isHost && !existing) {
    throw new EventLifecycleError('event-ended', 'This party has ended');
  }

  if (isHost || (existing && !leftLive)) {
    return enterExistingParticipant(eventData, uid);
  }

  if (isJoinApprovalRequired(eventData)) {
    return submitApprovalJoin(eventRef, eventData, uid, name, user, sessionEntry);
  }

  if (existing) {
    return enterExistingParticipant(eventData, uid, {
      name,
      photoURL: user?.photoURL,
    });
  }

  const newParticipant = {
    id: uid,
    name,
    role: 'guest',
    joinedAt: Date.now(),
    photoURL: sanitizeParticipantPhotoURL(user?.photoURL),
  };

  const joinPatch = { participants: arrayUnion(newParticipant) };
  if (Array.isArray(eventData.participantIds)) {
    joinPatch.participantIds = arrayUnion(uid);
  }
  if (Array.isArray(eventData.joinedParticipantIds)) {
    joinPatch.joinedParticipantIds = arrayUnion(uid);
  }
  await updateDoc(eventRef, joinPatch);

  const joinedEvent = {
    ...eventData,
    participants: [...(eventData.participants || []), newParticipant],
    participantIds: Array.isArray(eventData.participantIds)
      ? [...eventData.participantIds, uid]
      : eventData.participantIds,
    joinedParticipantIds: Array.isArray(eventData.joinedParticipantIds)
      ? [...eventData.joinedParticipantIds, uid]
      : eventData.joinedParticipantIds,
  };

  enterEvent(joinedEvent, eventData.hostId, 'guest');
  rememberSessionMembership(joinedEvent, 'guest').catch(() => {});
  markPresenceOnline({ eventId: joinedEvent.id, uid }).catch(() => {});

  return { eventData: joinedEvent, role: 'guest', rejoined: false, pending: false };
}

export const RESUME_STATUS = {
  ok: 'ok',
  invalidCode: 'invalid-code',
  notFound: 'not-found',
  notParticipant: 'not-participant',
  left: 'left',
};

/**
 * Re-enter the party named by the route.
 *
 * The event code comes from the URL, never from the stored pointer, so a tab
 * sitting on /party/A cannot be pulled into party B just because another tab
 * joined B afterwards.
 *
 * This never joins on the caller's behalf. Someone who is neither the host nor
 * an existing participant is reported as such so the UI can send them through
 * /join/{code}, where joining is an explicit act.
 */
export async function resumeEventByCode(rawCode) {
  const code = normalizeEventCode(rawCode);
  if (!code) return { status: RESUME_STATUS.invalidCode, eventData: null };

  const uid = await requireAuthUid();
  const snapshot = await getDoc(doc(db, 'events', code));

  if (!snapshot?.exists?.()) {
    // Only drop the pointer when it is the thing that went missing.
    if (loadSessionEventId() === code) clearSessionEventId();
    return { status: RESUME_STATUS.notFound, eventData: null };
  }

  const eventData = { id: snapshot.id, ...snapshot.data() };

  if (!canResumeEvent(uid, eventData)) {
    return { status: RESUME_STATUS.notParticipant, eventData };
  }

  if (isEventLive(eventData) && uid !== eventData.hostId) {
    const sessionEntry = await fetchSessionEntry(uid, eventData.id);
    if (isIntentionalLeave(sessionEntry)) {
      return { status: RESUME_STATUS.left, eventData };
    }
  }

  const participant = findParticipant(eventData.participants, uid);
  enterEvent(eventData, eventData.hostId, participant?.role);
  rememberSessionMembership(eventData, uid === eventData.hostId ? 'host' : 'guest').catch(() => {});
  if (isEventLive(eventData)) {
    markPresenceOnline({ eventId: eventData.id, uid }).catch(() => {});
  }

  return { status: RESUME_STATUS.ok, eventData };
}

/**
 * Host-only, live events: if this host's participant row is still the generic
 * "Guest" fallback, write the current Google display name so other guests see
 * it too. Never a local-only override.
 */
export async function reconcileHostParticipantDisplay() {
  const uid = useRoleStore.getState().userId || getCurrentUser()?.uid;
  const eventData = useEventStore.getState().eventData;
  if (!eventData?.id || !isEventLive(eventData) || eventData.hostId !== uid) return false;

  const user = getCurrentUser();
  const storeUser = useUserStore.getState().user;
  const storeProfile = useUserStore.getState().profile;
  const name = resolveDisplayName(user, null, {
    fallbackNames: [storeUser?.displayName, storeProfile?.displayName],
  });
  const photoURL = sanitizeParticipantPhotoURL(user?.photoURL || storeUser?.photoURL);

  const nextParticipants = buildReconciledHostParticipants(eventData.participants, {
    uid,
    hostId: eventData.hostId,
    name,
    photoURL,
  });
  if (!nextParticipants) return false;

  await updateDoc(doc(db, 'events', eventData.id), { participants: nextParticipants });
  useEventStore.getState().setEventData({ ...eventData, participants: nextParticipants });
  return true;
}

/**
 * Host-only: mark the event ended and leave it in place.
 * Never deletes the event document.
 */
export async function endParty(eventId) {
  const uid = await requireAuthUid();
  const code = normalizeEventCode(eventId) || useEventStore.getState().eventData?.id;
  if (!code) {
    throw new EventLifecycleError('missing-code', 'No party to end');
  }

  const eventRef = doc(db, 'events', code);
  const snapshot = await getDoc(eventRef);
  if (!snapshot?.exists?.()) {
    throw new EventLifecycleError('not-found', 'Event not found');
  }

  const eventData = { id: snapshot.id, ...snapshot.data() };
  if (eventData.hostId !== uid) {
    throw new EventLifecycleError('not-host', 'Only the host can end this party');
  }

  const endedAt = Date.now();
  const patch = eventEndedPayload({ endedBy: uid, endedAt });

  if (isEventLive(eventData)) {
    await updateDoc(eventRef, patch);
  }

  const ended = { ...eventData, ...patch };
  await rememberSessionMembership(ended, 'host');
  cleanupStores();

  return ended;
}

/**
 * Guest-only: leave the live room without ending the event or rewriting
 * participants. Historical membership stays on the event; the private session
 * index records the intentional departure.
 */
export async function leaveParty(eventId) {
  const uid = await requireAuthUid();
  const code = normalizeEventCode(eventId) || useEventStore.getState().eventData?.id;
  if (!code) {
    throw new EventLifecycleError('missing-code', 'No party to leave');
  }

  const snapshot = await getDoc(doc(db, 'events', code));
  if (!snapshot?.exists?.()) {
    throw new EventLifecycleError('not-found', 'Event not found');
  }

  const eventData = { id: snapshot.id, ...snapshot.data() };
  if (eventData.hostId === uid) {
    throw new EventLifecycleError('not-guest', 'The host ends the party rather than leaving it');
  }

  if (
    isEventLive(eventData)
    && Array.isArray(eventData.participantIds)
    && eventData.participantIds.includes(uid)
  ) {
    try {
      await updateDoc(doc(db, 'events', eventData.id), {
        participantIds: eventData.participantIds.filter((id) => id !== uid),
      });
    } catch (error) {
      console.error(
        'Leave Party: removing vibe membership did not succeed:',
        describeError(error),
      );
    }
  }

  const offlinePresence = await markPresenceOffline({ eventId: eventData.id, uid });
  if (!offlinePresence) {
    console.error(
      'Leave Party: presence offline write did not succeed; other clients may still show this guest as online.',
    );
  }
  await markSessionLeft({
    uid,
    eventId: eventData.id,
    title: eventData.title || '',
    role: 'guest',
  });
  cleanupStores();
  return { eventData, left: true };
}
